/**
 * @description
 * Activity heatmap component showing domain activity intensity over time.
 * Calendar-style visualization with color-coded cells based on activity levels.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/ActivityHeatmap.test.tsx
 */

import { useMemo, useState } from 'react';
import type { TimeSeriesAggregation } from '../../../types/vscode-lens';
import { formatTimeLabel } from '../../utils/timeSeriesUtils';

interface ActivityHeatmapProps {
  /** Time-series aggregation data */
  data: TimeSeriesAggregation;
  
  /** Domains to display */
  domains: string[];
  
  /** Granularity (only 'hour' and 'day' supported for heatmap) */
  granularity: 'hour' | 'day';
  
  /** Callback when cell is clicked */
  onCellClick?: (timestamp: number, domain: string, count: number) => void;
}

interface HeatmapCell {
  timestamp: number;
  domain: string;
  count: number;
  intensity: number; // 0-1 normalized value
}

/**
 * Activity heatmap visualization
 */
export function ActivityHeatmap({
  data,
  domains,
  granularity,
  onCellClick,
}: ActivityHeatmapProps) {
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null);

  // Prepare heatmap data
  const heatmapData = useMemo(() => {
    // Create a map of timestamp-domain to count
    const cellMap = new Map<string, number>();
    let maxCount = 0;

    data.dataPoints.forEach(point => {
      if (!domains.includes(point.domain)) return;

      const key = `${point.timestamp}-${point.domain}`;
      cellMap.set(key, point.count);
      maxCount = Math.max(maxCount, point.count);
    });

    // Get unique timestamps
    const timestamps = Array.from(
      new Set(data.dataPoints.map(p => p.timestamp))
    ).sort((a, b) => a - b);

    // Create cells for each timestamp-domain combination
    const cells: HeatmapCell[] = [];
    timestamps.forEach(timestamp => {
      domains.forEach(domain => {
        const key = `${timestamp}-${domain}`;
        const count = cellMap.get(key) || 0;
        const intensity = maxCount > 0 ? count / maxCount : 0;

        cells.push({
          timestamp,
          domain,
          count,
          intensity,
        });
      });
    });

    return { cells, timestamps, maxCount };
  }, [data.dataPoints, domains]);

  // Get color for intensity
  const getColor = (intensity: number): string => {
    if (intensity === 0) return 'bg-gray-100 dark:bg-gray-800';
    if (intensity < 0.2) return 'bg-blue-200 dark:bg-blue-900';
    if (intensity < 0.4) return 'bg-blue-300 dark:bg-blue-800';
    if (intensity < 0.6) return 'bg-blue-400 dark:bg-blue-700';
    if (intensity < 0.8) return 'bg-blue-500 dark:bg-blue-600';
    return 'bg-blue-600 dark:bg-blue-500';
  };

  // Calculate cell size based on number of timestamps
  const cellSize = useMemo(() => {
    const count = heatmapData.timestamps.length;
    if (count > 48) return 'w-3 h-8';
    if (count > 24) return 'w-4 h-8';
    if (count > 12) return 'w-6 h-8';
    return 'w-8 h-8';
  }, [heatmapData.timestamps.length]);

  if (domains.length === 0 || heatmapData.timestamps.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground">
        <p>No activity data available for heatmap</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Activity Heatmap</h3>
        <p className="text-sm text-muted-foreground">
          {granularity === 'hour' ? 'Hourly' : 'Daily'} activity intensity by domain
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
        <span>Less</span>
        <div className="flex gap-1">
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((intensity, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded ${getColor(intensity)}`}
            />
          ))}
        </div>
        <span>More</span>
        <span className="ml-4">Max: {heatmapData.maxCount.toLocaleString()}</span>
      </div>

      {/* Heatmap Grid */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Time labels */}
          <div className="flex mb-2">
            <div className="w-32 flex-shrink-0" /> {/* Space for domain labels */}
            <div className="flex gap-1 overflow-x-auto">
              {heatmapData.timestamps.map((timestamp, i) => {
                // Show label every N timestamps based on count
                const showLabel = 
                  i === 0 || 
                  i === heatmapData.timestamps.length - 1 ||
                  (heatmapData.timestamps.length <= 24 && i % 4 === 0) ||
                  (heatmapData.timestamps.length > 24 && i % 8 === 0);

                return (
                  <div
                    key={timestamp}
                    className={`${cellSize} flex-shrink-0`}
                  >
                    {showLabel && (
                      <div className="text-xs text-muted-foreground transform -rotate-45 origin-top-left whitespace-nowrap">
                        {formatTimeLabel(timestamp, granularity)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Heatmap rows */}
          {domains.map(domain => (
            <div key={domain} className="flex mb-1">
              {/* Domain label */}
              <div className="w-32 flex-shrink-0 pr-2 text-sm truncate flex items-center" title={domain}>
                {domain}
              </div>

              {/* Cells */}
              <div className="flex gap-1">
                {heatmapData.timestamps.map(timestamp => {
                  const cell = heatmapData.cells.find(
                    c => c.timestamp === timestamp && c.domain === domain
                  );

                  if (!cell) return null;

                  return (
                    <button
                      key={`${timestamp}-${domain}`}
                      className={`${cellSize} flex-shrink-0 rounded ${getColor(cell.intensity)} hover:ring-2 hover:ring-primary transition-all cursor-pointer`}
                      onMouseEnter={() => setHoveredCell(cell)}
                      onMouseLeave={() => setHoveredCell(null)}
                      onClick={() => onCellClick?.(cell.timestamp, cell.domain, cell.count)}
                      title={`${domain} at ${formatTimeLabel(timestamp, granularity)}: ${cell.count} records`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div className="mt-4 p-3 rounded-lg border bg-accent/50">
          <div className="text-sm">
            <div className="font-medium">{hoveredCell.domain}</div>
            <div className="text-muted-foreground">
              {formatTimeLabel(hoveredCell.timestamp, granularity)}
            </div>
            <div className="mt-1 font-semibold">
              {hoveredCell.count.toLocaleString()} records
            </div>
            <div className="text-xs text-muted-foreground">
              {(hoveredCell.intensity * 100).toFixed(1)}% of max activity
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 text-xs text-muted-foreground">
        <p>
          Showing {domains.length} domain{domains.length !== 1 ? 's' : ''} across{' '}
          {heatmapData.timestamps.length} time period{heatmapData.timestamps.length !== 1 ? 's' : ''}
        </p>
        <p className="mt-1">
          Click on any cell to drill down into that time period
        </p>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */